/* eslint-disable no-console */
import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getCategoriesDataView,
    getCategoryState,
    getSubCategoryIds,
} from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tecumseh/theme/design";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    showImages = "showImages",
    showOnlyTopLevelCategories = "showOnlyTopLevelCategories",
    title = "title",
    selectedCategory = "selectedCategory",
    selectColumn = "selectColumn",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showImages]: boolean;
        [fields.showOnlyTopLevelCategories]: boolean;
        [fields.title]: string;
        [fields.selectedCategory]: LinkFieldValue;
        [fields.selectColumn]: number;
    };
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
    const selectedCategoryID = props.fields.selectedCategory.value;
    const columnCount = props.fields.selectColumn;
    const getCategoriesParameter = {
        maxDepth: props.fields.showOnlyTopLevelCategories ? 1 : 2,
        includeStartCategory: false,
        expand: ["properties"]
    };
    return {
        categoriesDataView: getCategoriesDataView(state, getCategoriesParameter),
        getCategoriesParameter,
        topLevelCategoryIds: getSubCategoryIds(state, emptyGuid),
        selectedCategoryID,
        columnCount,
    };
};

const mapDispatchToProps = {
    loadCategories,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme>;

export interface HomepageCategoryListStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    container?: GridContainerProps;
    categoryItem?: GridItemProps;
    categoryMainGridItem?: GridItemProps;
    innerContainer?: GridContainerProps;
    categoryImageItem?: GridItemProps;
    categoryImageLink?: LinkPresentationProps;
    categoryImage?: LazyImageProps;
    categoryNameLinkItem?: GridItemProps;
    categoryNameLink?: LinkPresentationProps;
    categoryName?: TypographyProps;
    subCategoryNameLinkItem?: GridItemProps;
    subCategoryNameLink?: LinkPresentationProps;
    viewMoreLinkItem?: GridItemProps;
    viewMoreLink?: LinkPresentationProps;
    titleGridItem?: GridItemProps;
    circleIcon?: InjectableCss;
    textCircleContainer?: InjectableCss;
    TitleStyle?: TypographyProps;
}

export const homepageCategoryListStyles: HomepageCategoryListStyles = {
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    container: {
        gap: 20,
        css: css`
            margin: 15px auto 50px auto;
            > div[class^="GridOffset"] {
                width: 100%;
                margin: 0 auto;
            }
        `,
    },
    categoryItem: {
        // width is dynamic here
        width: 0,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 5px;
                        `,
                        css`
                            padding: 5px;
                        `,
                        null,
                        null,
                    ],
                    "minMax",
                )}
        `,
    },
    categoryMainGridItem: {
        css: css`
            flex-direction: column;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 50% !important;
                            max-width: 50% !important;
                            flex-basis: 50% !important;
                        `,
                        css`
                            width: 50% !important;
                            max-width: 50% !important;
                            flex-basis: 50% !important;
                        `,
                        css`
                            width: 50% !important;
                            max-width: 50% !important;
                            flex-basis: 50% !important;
                        `,
                        null,
                    ],
                    "minMax",
                )};
        `,
    },
    innerContainer: {
        gap: 10,
        css: css`
            border-radius: 7px;
            border: 1px solid ${COLORS.gainsboro};
            opacity: 1;
            background-color: ${COLORS.background.base};
            /* height: 300px; */
        `,
    },
    categoryImageItem: {
        width: 12,
        css: css`
            height: 160px;
            justify-content: center;
        `,
    },
    categoryImage: {
        width: "160px",
        height: "160px",
    },
    categoryNameLinkItem: {
        width: 12,
        css: css`
            padding-bottom: 8px;
        `,
    },
    categoryName: {
        css: css`
            opacity: 1;
            color: ${COLORS.white};
            font-family: ${TYPOGRAPHY.fontFamily.dosis};
            font-size: ${TYPOGRAPHY.size.small};
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            font-style: normal;
            /* text-transform: uppercase; */
            text-align: left;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-size: ${TYPOGRAPHY.size.tiny};
                        `,
                        css`
                            font-size: ${TYPOGRAPHY.size.tiny};
                        `,
                        null,
                        css`
                            font-size: ${TYPOGRAPHY.size.tiny};
                        `,
                    ],
                    "minMax",
                )};
        `,
    },
    subCategoryNameLinkItem: {
        width: 12,
    },
    viewMoreLinkItem: {
        width: 12,
        css: css`
            font-weight: ${TYPOGRAPHY.weight.bold};
            padding-top: 10px;
        `,
    },
    titleGridItem: {
        width: 12,
        css: css`
            opacity: 1;
            color: ${getColor("primary")};
            font-family: ${TYPOGRAPHY.fontFamily.barlow};
            font-size: ${TYPOGRAPHY.size.mLarge};
            font-weight: ${TYPOGRAPHY.weight.baseBold};
            font-style: normal;
            letter-spacing: ${TYPOGRAPHY.letterSpacing.smallish};
            text-align: center;
            text-transform: uppercase;
            justify-content: center;
            padding: 10px;
        `,
    },
    TitleStyle: {
        variant: "h2",
        css: css`
            opacity: 1;
            color: ${getColor("primary")};
            font-family: ${TYPOGRAPHY.fontFamily.barlow};
            font-weight: ${TYPOGRAPHY.weight.baseBold};
            font-style: normal;
            letter-spacing: ${TYPOGRAPHY.letterSpacing.smallish};
            text-align: center;
            text-transform: uppercase;
            justify-content: center;
            padding: 10px;
        `,
    },
    categoryNameLink: {
        css: css`
            border-radius: 0px 22.5px 22.5px 0px;
            opacity: 1;
            /* background-color: ${COLORS.background.primaryBlue}; */
            background-color: ${getColor("primary")};
            padding: 0 10px;
            margin-bottom: 5px;
            &:hover {
                .circleIcon {
                    background: ${COLORS.yellow};
                    border: 0.25px solid rgba(17, 41, 69, 0.22);
                    box-shadow: inset 0 0 2px 0 rgba(0, 0, 0, 0.64);
                }
            }
        `,
    },
    circleIcon: {
        css: css`
            width: 11px;
            margin-left: 6px !important;
            height: 11px;
            border-radius: 50%;
            display: inline-block;
            position: relative;
            margin: auto;
            left: 0px;
            border-radius: 5.499547px;
            border: 0.25px solid rgba(17, 41, 69, 0.219214);
            opacity: 1;
            background-color: ${COLORS.background.whiteSmoke};
            box-shadow: 0px 0px 2px rgb(0 0 0 / 64%);
        `,
    },
    textCircleContainer: {
        css: css`
            display: flex;
        `,
    },
};

const styles = homepageCategoryListStyles;

class TECHomepageCategoryList extends React.Component<Props> {
    container = React.createRef<HTMLDivElement>();
    categoryItemStyles: GridItemProps = { ...styles.categoryItem };

    UNSAFE_componentWillMount(): void {
        const { categoriesDataView, getCategoriesParameter, loadCategories } = this.props;
        if (!categoriesDataView.isLoading && !categoriesDataView.value) {
            loadCategories(getCategoriesParameter);
        }
    }

    recalculateWidth = () => {
        if (!this.container.current) {
            return;
        }

        const { breakpoints } = this.props.theme;
        const { clientWidth } = this.container.current;
        let width: GridWidths;
        if (clientWidth < breakpoints.values[1]) {
            width = 6;
        } else if (clientWidth < breakpoints.values[2]) {
            width = 6;
        } else if (clientWidth < breakpoints.values[3]) {
            width = 4;
        } else {
            width = 2;
        }

        if (width === this.categoryItemStyles.width) {
            return;
        }

        this.categoryItemStyles.width = width;
        this.forceUpdate();
    };

    componentDidMount() {
        this.recalculateWidth();
        window.addEventListener("resize", this.recalculateWidth, true);
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.recalculateWidth);
    }

    componentDidUpdate(prevProps: Props) {
        if (!prevProps.categoriesDataView.value && this.props.categoriesDataView.value) {
            this.recalculateWidth();
        }
    }

    render() {
        const {
            categoriesDataView,
            topLevelCategoryIds,
            selectedCategoryID,
            columnCount,
            fields: { showImages, title },
        } = this.props;

        if (categoriesDataView.isLoading) {
            return (
                <StyledWrapper {...styles.centeringWrapper}>
                    <LoadingSpinner {...styles.spinner} />
                </StyledWrapper>
            );
        }

        if (!categoriesDataView.value || !topLevelCategoryIds) {
            return null;
        }

        const categories = categoriesDataView.value.filter(o => o.id === selectedCategoryID);

        return (
            <StyledWrapper ref={this.container}>
                <GridItem {...styles.titleGridItem}>
                    {title && (
                        <Typography as="h2" {...styles.TitleStyle}>
                            {title}
                        </Typography>
                    )}
                </GridItem>
                <GridContainer {...styles.container}>
                    {categories.map(category => (
                        <>
                            {category.subCategoryIds &&
                                category.subCategoryIds.map(subCategoryId => (
                                    <GridItem
                                        key={category.id.toString()}
                                        {...styles.categoryMainGridItem}
                                        style={{
                                            width: `calc(100% / ${this.props.columnCount})`,
                                            maxWidth: `calc(100% / ${this.props.columnCount})`,
                                            flexBasis: `calc(100% / ${this.props.columnCount})`,
                                        }}
                                    >
                                        <SubCategoryLink categoryId={subCategoryId} />
                                    </GridItem>
                                ))}
                        </>
                    ))}
                </GridContainer>
            </StyledWrapper>
        );
    }
}

const SubCategoryLinkView = ({ category }: ReturnType<typeof linkMapStateToProps>) => {
    if (!category) {
        return null;
    }

    return (
        <GridContainer {...styles.innerContainer}>
            <GridItem {...styles.categoryImageItem}>
                {category.path && (
                    <Link href={category.path} {...styles.categoryImageLink}>
                        <LazyImage
                            src={category.smallImagePath}
                            {...styles.categoryImage}
                            altText={category.imageAltText}
                        />
                    </Link>
                )}
            </GridItem>
            <GridItem {...styles.categoryNameLinkItem}>
                <Link href={category.path} {...styles.categoryNameLink}>
                    <StyledWrapper {...styles.textCircleContainer}>
                        <Typography {...styles.categoryName}>{category.shortDescription}</Typography>
                        <Typography {...styles.circleIcon} className="circleIcon"></Typography>
                    </StyledWrapper>
                </Link>
            </GridItem>
        </GridContainer>
    );
};

const linkMapStateToProps = (state: ApplicationState, ownProps: { categoryId: string }) => ({
    category: getCategoryState(state, ownProps.categoryId).value,
});

const SubCategoryLink = connect(linkMapStateToProps)(SubCategoryLinkView);

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(TECHomepageCategoryList)),
    definition: {
        group: "Categories",
        icon: "list-ul",
        displayName: "TECHomepageCategoryList",
        fieldDefinitions: [
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 1,
            },
            {
                name: fields.showImages,
                displayName: "Show Images",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.selectColumn,
                displayName: "Select Column Width",
                editorTemplate: "DropDownField",
                defaultValue: 2,
                options: [1, 2, 3, 4, 5, 6].map(value => ({ value })),
                fieldType: "General",
            },
            {
                fieldType: "General",
                name: fields.selectedCategory,
                displayName: "Select Category",
                editorTemplate: "LinkField",
                defaultValue: { type: "Category", value: "" },
                allowUrls: () => false,
                allowCategories: () => true,
            },
        ],
    },
};

export default widgetModule;
